import { PAGE_ROUTES } from '../constants/pageRoutes';

/**
 * Central Notification Route Resolver
 * Maps incoming notification payload types, reference types, and IDs to the exact frontend page routes.
 * Uses PAGE_ROUTES constants for route definitions.
 */

// CURRENT ACTIVE BACKEND NOTIFICATION TYPES
export const NOTIFICATION_TYPE = Object.freeze({
	PROCESS_ISSUE_ASSIGNED: 'PROCESS_ISSUE_ASSIGNED',
	PROCESS_RECEIVE_CREATED: 'PROCESS_RECEIVE_CREATED',
	PROCESS_ISSUE_TRANSFERRED: 'PROCESS_ISSUE_TRANSFERRED',
	PROCESS_ISSUE_CANCELLED: 'PROCESS_ISSUE_CANCELLED',
} as const);

export interface INotificationPayloadData {
	type?: string;
	notification_type?: string;
	id?: string | number;
	reference_type?: string;
	reference_id?: string | number;
	process_id?: string | number;
	processId?: string | number;
	process?: { id?: string | number; name?: string; [key: string]: any } | null;
	issue?: { process_id?: string | number; [key: string]: any } | null;
	params?: { process_id?: string | number; [key: string]: any } | null;
	url?: string;
	route?: string;
	data?: any;
	[key: string]: any;
}

/**
 * Helper to dynamically replace route params (e.g. :id, :processId)
 */
export const buildDynamicRoute = (
	template: string,
	params: Record<string, string | number | undefined | null>,
): string => {
	let route = template;
	Object.entries(params).forEach(([key, val]) => {
		if (val !== undefined && val !== null && val !== '') {
			route = route.replace(`:${key}`, String(val));
		}
	});
	return route.startsWith('/') ? route : `/${route}`;
};

const toValidId = (val: any): number | string | null => {
	if (val === undefined || val === null || val === '') return null;
	const num = Number(val);
	if (!isNaN(num) && num > 0) return num;
	if (typeof val === 'string' && val.trim().length > 0) return val.trim();
	return null;
};

export const resolveNotificationRoute = (data?: INotificationPayloadData): string => {
	if (!data) return '/';

	// Direct explicit URL provided in payload fallback
	if (data.url && typeof data.url === 'string' && data.url.startsWith('/')) {
		return data.url;
	}
	if (data.route && typeof data.route === 'string' && data.route.startsWith('/')) {
		return data.route;
	}

	const rawType = String(
		data.type ||
		data.notification_type ||
		data.data?.type ||
		data.data?.notification_type ||
		''
	).toUpperCase().trim();

	const refType = String(
		data.reference_type ||
		data.referenceType ||
		data.data?.reference_type ||
		data.data?.referenceType ||
		''
	).toLowerCase().trim();

	// Extract process ID from all possible backend payload locations
	const rawProcessId =
		data.process_id ??
		data.processId ??
		data.process?.id ??
		data.issue?.process_id ??
		data.params?.process_id ??
		data.params?.processId ??
		data.data?.process_id ??
		data.data?.processId ??
		data.data?.process?.id ??
		data.data?.issue?.process_id ??
		data.data?.params?.process_id ??
		(refType === 'processes' || refType === 'process' ? data.reference_id ?? data.id : null);

	const targetProcessId = toValidId(rawProcessId);

	// Extract reference ID (issue id, receipt id, challan id, etc.)
	const targetReferenceId = toValidId(
		data.reference_id ??
		data.referenceId ??
		data.data?.reference_id ??
		data.data?.referenceId ??
		data.id ??
		data.data?.id
	);

	// 1. Process Issue / Receive / Transfer / Cancel
	const isProcessRelated =
		rawType === NOTIFICATION_TYPE.PROCESS_ISSUE_ASSIGNED ||
		rawType === NOTIFICATION_TYPE.PROCESS_RECEIVE_CREATED ||
		rawType === NOTIFICATION_TYPE.PROCESS_ISSUE_TRANSFERRED ||
		rawType === NOTIFICATION_TYPE.PROCESS_ISSUE_CANCELLED ||
		rawType.startsWith('PROCESS_') ||
		refType === 'process_issues' ||
		refType === 'process_receipts' ||
		refType === 'processes' ||
		refType === 'process' ||
		refType === 'process_flow' ||
		refType === 'process_work';

	if (isProcessRelated) {
		if (targetProcessId) {
			return buildDynamicRoute(PAGE_ROUTES.PROCESS_ISSUE, { processId: targetProcessId });
		}
		if (targetReferenceId && (refType === 'process_issues' || rawType.includes('ISSUE'))) {
			return buildDynamicRoute(PAGE_ROUTES.PROCESS_ISSUE_VIEW, { id: targetReferenceId });
		}
		return `/${PAGE_ROUTES.PROCESS_FLOW}`;
	}

	// 2. Fabric Inwards
	if (
		rawType.includes('FABRIC_INWARD') ||
		refType === 'fabric_inward' ||
		refType === 'fabric_inwards' ||
		refType === 'fabric-inwards'
	) {
		return targetReferenceId
			? buildDynamicRoute(PAGE_ROUTES.FABRIC_INWARDS_VIEW, { id: targetReferenceId })
			: `/${PAGE_ROUTES.FABRIC_INWARDS}`;
	}

	// 3. Fabric Stock
	if (rawType.includes('FABRIC_STOCK') || refType === 'fabric_stock' || refType === 'fabric-stock') {
		return `/${PAGE_ROUTES.FABRIC_STOCK}`;
	}

	// 4. Cutting Stock
	if (rawType.includes('CUTTING_STOCK') || refType === 'cutting_stock' || refType === 'cutting-stock') {
		return `/${PAGE_ROUTES.CUTTING_STOCK}`;
	}

	// 5. Cutting
	if (rawType.includes('CUTTING') || refType === 'cutting' || refType === 'cuttings') {
		return targetReferenceId
			? buildDynamicRoute(PAGE_ROUTES.CUTTING_VIEW, { id: targetReferenceId })
			: `/${PAGE_ROUTES.CUTTING}`;
	}

	// 6. Bundling
	if (rawType.includes('BUNDLING') || refType === 'bundling' || refType === 'bundle') {
		return targetReferenceId
			? buildDynamicRoute(PAGE_ROUTES.BUNDLING_VIEW, { id: targetReferenceId })
			: `/${PAGE_ROUTES.BUNDLING}`;
	}

	// 7. Delivery Challan
	if (
		rawType.includes('DELIVERY_CHALLAN') ||
		rawType.includes('CHALLAN') ||
		refType === 'delivery_challan' ||
		refType === 'delivery-challan'
	) {
		return targetReferenceId
			? buildDynamicRoute(PAGE_ROUTES.DELIVERY_CHALLAN_VIEW, { id: targetReferenceId })
			: `/${PAGE_ROUTES.DELIVERY_CHALLAN}`;
	}

	// 8. Labour Hisaab
	if (rawType.includes('LABOUR_HISAAB') || refType === 'labour_hisaab' || refType === 'labour-hisaab') {
		return targetReferenceId
			? buildDynamicRoute(PAGE_ROUTES.LABOUR_HISAAB_VIEW, { id: targetReferenceId })
			: `/${PAGE_ROUTES.LABOUR_HISAAB}`;
	}

	// 9. Ready Stock
	if (rawType.includes('READY_STOCK') || refType === 'ready_stock' || refType === 'ready-stock') {
		return `/${PAGE_ROUTES.READY_STOCK}`;
	}

	// 10. Sales Invoice
	if (rawType.includes('SALES_INVOICE') || refType === 'sales_invoice' || refType === 'sales-invoice') {
		return targetReferenceId
			? buildDynamicRoute(PAGE_ROUTES.SALES_INVOICE_VIEW, { id: targetReferenceId })
			: `/${PAGE_ROUTES.SALES_INVOICE}`;
	}

	// 11. Purchase Invoice
	if (rawType.includes('PURCHASE_INVOICE') || refType === 'purchase_invoice' || refType === 'purchase-invoice') {
		return targetReferenceId
			? buildDynamicRoute(PAGE_ROUTES.PURCHASE_INVOICE_VIEW, { id: targetReferenceId })
			: `/${PAGE_ROUTES.PURCHASE_INVOICE}`;
	}

	return '/';
};

export default resolveNotificationRoute;
